/**
 * AUTH CONTEXT v2.1
 * ✅ fix 1: analyticsService.setUserProperties() conectado al login
 * ✅ fix 2: analyticsService.clearUserData() conectado al logout
 * ✅ fix 3: trackRetentionPing llamado al autenticar (D1/D7 beta)
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, memo } from 'react';
import { auth, db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { setSentryUser } from '../utils/sentry';
import analyticsService from '../services/analyticsService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // ============================================
  // HELPERS
  // ============================================

  // fix 1 + fix 3: conectar analytics al usuario autenticado
  const updateAnalytics = useCallback((firebaseUser) => {
    if (firebaseUser) {
      setSentryUser(firebaseUser);

      // fix 1: setUserProperties con user_id real
      analyticsService.setUserProperties(firebaseUser.uid, {
        email_domain: firebaseUser.email?.split('@')[1] || 'unknown',
      });

      // fix 3: retention ping — usa creationTime del usuario de Firebase Auth
      analyticsService.trackRetentionPing(
        firebaseUser.metadata?.creationTime
      );
    } else {
      setSentryUser(null);
      // fix 2: limpiar analytics en logout
      analyticsService.clearUserData();
    }
  }, []);

  const checkAdminStatus = useCallback(async (firebaseUser) => {
    try {
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const adminStatus = userData.isAdmin === true;
        setIsAdmin(adminStatus);

        console.log('👤 Datos de usuario:', { email: firebaseUser.email, isAdmin: adminStatus });
        if (adminStatus) console.log('👑 Usuario es ADMIN');

        window.__AUTH_DEBUG = { user: firebaseUser.email, isAdmin: adminStatus, userData };
        return adminStatus;
      } else {
        console.warn('⚠️ Documento de usuario no existe en Firestore');
        setIsAdmin(false);
        return false;
      }
    } catch (error) {
      console.error('❌ Error leyendo datos de usuario:', error);
      setIsAdmin(false);
      return false;
    }
  }, []);

  const login = useCallback(async () => {
    try {
      setError(null);
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Usando Login Nativo...');
        const googleUser = await GoogleAuth.signIn();
        const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
        const result = await signInWithCredential(auth, credential);
        console.log('✅ Login Nativo Éxito:', result.user.email);
        return { success: true, user: result.user };
      } else {
        console.log('🌐 Usando Login Web...');
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        return { success: true, user: result.user };
      }
    } catch (err) {
      console.error('❌ Error Login:', err);
      let msg = 'Error al iniciar sesión';
      if (err.message?.includes('12500')) msg = 'Error de Google Play Services';
      if (err.message?.includes('10'))    msg = 'Error de Configuración (SHA-1)';
      if (err.code === 'auth/popup-closed-by-user') msg = 'Cancelado por usuario';
      setError(msg);
      return { success: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      if (Capacitor.isNativePlatform()) await GoogleAuth.signOut();
      return { success: true };
    } catch (err) {
      console.error('Error logout:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const refreshUserProfile = useCallback(async () => {
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsAdmin(userData.isAdmin === true);
        }
        setUser({ ...auth.currentUser });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error refrescando usuario", error);
      return false;
    }
  }, []);

  // ============================================
  // INICIALIZACIÓN GOOGLE AUTH NATIVO
  // ============================================
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      console.log('🔧 Inicializando Google Auth Nativo...');
      GoogleAuth.initialize({
        clientId: '1076691975230-34n9rippar11rgdlq9c9orm4nvnbmuct.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
    }
  }, []);

  // ============================================
  // LISTENER DE AUTENTICACIÓN
  // ============================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔐 Estado de auth:', firebaseUser?.email || 'No autenticado');
      setUser(firebaseUser);
      updateAnalytics(firebaseUser);
      if (firebaseUser) {
        await checkAdminStatus(firebaseUser);
      } else {
        setIsAdmin(false);
        window.__AUTH_DEBUG = null;
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [updateAnalytics, checkAdminStatus]);

  // ============================================
  // VALUE
  // ============================================
  const value = useMemo(() => ({
    user,
    loading,
    error,
    isAdmin,
    login,
    logout,
    refreshUserProfile,
    isAuthenticated: !!user
  }), [user, loading, error, isAdmin, login, logout, refreshUserProfile]);

  if (loading) return <LoadingState />;

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const LoadingState = memo(function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
    </div>
  );
});